import React, {useState} from 'react';

import cs from 'classnames';
import {Provider} from 'react-redux';

import {SearchItemsSelectSingle} from 'app/search/components/SearchItemsSelect';
import store from 'app/store/AppStore';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import type {SearchSerializedType} from 'sweet-types';
type FroalaInputFieldProps = {
  type?: string;
  className?: string;
  id: string;
  label: string;
  value?: string;
  onChange: (event: React.SyntheticEvent<HTMLInputElement>) => unknown;
  placeholder?: string;
  tabIndex?: number;
};

const FroalaInputField = ({
  id,
  className,
  label,
  value = '',
  type = 'text',
  ...other
}: FroalaInputFieldProps) => (
  <div className="fr-input-line">
    <input
      className={cs(
        'fr-link-attr',
        {
          'fr-not-empty': value !== '',
        },
        className
      )}
      id={id}
      value={value}
      type={type}
      {...other}
    />
    <label htmlFor={id}>{label}</label>
  </div>
);

type Props = {
  editor: any;
  plugin: {
    hidePopup: () => void;
  };
};
export function AddDocumentLinkPopup({editor, plugin}: Props) {
  const [linkText, setLinkText] = useState<string>(editor.selection.text());
  const [linkHref, setLinkHref] = useState<string>(editor.selection.text());
  const [selectedDoc, selectDoc] = useState<SearchSerializedType | null>(null);

  const getUniqueId = (slug: string) => `${slug}${editor.id}`;

  const documentSelect = (doc: SearchSerializedType | null) => {
    selectDoc(doc);

    if (doc == null) {
      setLinkText('');
    } else {
      setLinkHref(doc.html_url);
      setLinkText(
        linkText || getObjectName(doc.name, doc.content_type, false).toString()
      );
    }

    return Promise.resolve(true);
  };

  const insertLink = () => {
    if (selectedDoc == null) {
      return;
    }

    if (editor.$el.find('.fr-marker')) {
      editor.events.disableBlur();
      editor.selection.restore();
    }

    editor.undo.saveStep();
    editor.html.insert(
      `<a href="${linkHref}" 'target="_blank">${linkText}</a>`
    );
    editor.undo.saveStep();
    plugin.hidePopup();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkText(e.target.value);
  };
  return (
    <Provider store={store}>
      <div className="fr-popup fr-desktop fr-active">
        <div className="fr-active fr-layer">
          <div className="fr-input-line">
            <p className="ml-1">
              {gt('Search for procedures, policies and processes')}
            </p>

            <SearchItemsSelectSingle
              selectProps={{
                autoFocus: true,
              }}
              placeholder={gt('Type document name...')}
              params={{
                ct: 'procedure,policy,proceduregroup',
                ordering: 'name',
              }}
              lazyFetch={true}
              selected={selectedDoc}
              onChange={documentSelect}
            />
          </div>

          <FroalaInputField
            tabIndex={3}
            onChange={handleChange}
            value={linkText}
            id={getUniqueId('proc-link-text')}
            placeholder={gt('Link text')}
            label={gt('Link text')}
          />

          <div className="fr-action-buttons">
            <button
              disabled={selectedDoc == null}
              onClick={insertLink}
              tabIndex={4}
              className="fr-command"
            >
              {gt('Insert')}
            </button>
          </div>
        </div>
      </div>
    </Provider>
  );
}
